/**
 * 角色权限管理 Tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { systemConfigApi, getErrorMessage } from '../services/api';
import { UserRole } from '../types';

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: '管理员',
  enterprise: '企业用户',
  agent: '代理用户',
  normal: '普通用户',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: '系统最高权限，可管理所有项目、用户和系统配置',
  enterprise: '企业用户，可创建和管理自己的项目',
  agent: '代理用户，可创建和管理自己的项目',
  normal: '普通用户，可创建和管理自己的项目',
};

interface PermissionDef {
  key: string;
  label: string;
  description: string;
}

function RolesTab() {
  const [rolePerms, setRolePerms] = useState<Record<string, string[]>>({});
  const [allPerms, setAllPerms] = useState<PermissionDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [editPerms, setEditPerms] = useState<string[]>([]);

  const loadPerms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemConfigApi.getRolePermissions();
      if (res.data.success) {
        setRolePerms(res.data.permissions);
        setAllPerms(res.data.all_permissions);
      }
    } catch {
      // 静默
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPerms(); }, [loadPerms]);

  const startEdit = (role: UserRole) => {
    setEditingRole(role);
    setEditPerms([...(rolePerms[role] || [])]);
    setMsg(null);
  };

  const cancelEdit = () => { setEditingRole(null); setEditPerms([]); };

  const togglePerm = (key: string) => {
    setEditPerms((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const savePerms = async (role: UserRole) => {
    setSaving(role);
    setMsg(null);
    try {
      const res = await systemConfigApi.setRolePermissions(role, editPerms);
      if (res.data.success) {
        setRolePerms(res.data.permissions);
        setMsg({ type: 'success', text: `角色「${ROLE_LABELS[role]}」权限已更新` });
        setEditingRole(null);
        setTimeout(() => setMsg(null), 3000);
      }
    } catch (err) {
      setMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
      setTimeout(() => setMsg(null), 4000);
    } finally {
      setSaving(null);
    }
  };

  const roleEntries = Object.entries(ROLE_LABELS) as [UserRole, string][];

  if (loading) {
    return <section className="text-center py-12 text-sm text-gray-400">加载中...</section>;
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">角色权限管理</h3>
          <p className="text-xs text-gray-500 mt-0.5">为不同角色配置操作权限，修改后立即生效。</p>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 rounded-lg border px-4 py-2 text-sm ${
          msg.type === 'success' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {msg.text}
        </div>
      )}

      <div className="space-y-4">
        {roleEntries.map(([role, label]) => {
          const isEditing = editingRole === role;
          const perms = isEditing ? editPerms : (rolePerms[role] || []);
          const isSaving = saving === role;
          const colorClasses: Record<string, string> = {
            admin: 'bg-red-100 text-red-700',
            enterprise: 'bg-blue-100 text-blue-700',
            agent: 'bg-purple-100 text-purple-700',
            normal: 'bg-gray-200 text-gray-600',
          };

          return (
            <div key={role} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses[role]}`}>
                    {label}
                  </span>
                  <span className="text-sm text-gray-600">{ROLE_DESCRIPTIONS[role]}</span>
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <button onClick={cancelEdit} className="px-3 py-1 text-xs font-medium rounded text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors">取消</button>
                    <button onClick={() => savePerms(role)} disabled={isSaving} className="px-3 py-1 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {isSaving ? '保存中...' : '保存'}
                    </button>
                  </div>
                ) : (
                  <button onClick={() => startEdit(role)} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors">
                    <PencilSquareIcon className="w-3.5 h-3.5 mr-1" />
                    编辑权限
                  </button>
                )}
              </div>
              <div className="px-4 py-3">
                {allPerms.length === 0 ? (
                  <p className="text-xs text-gray-400">暂无权限配置项</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {allPerms.map((perm) => {
                      const checked = perms.includes(perm.key);
                      return (
                        <label
                          key={perm.key}
                          className={`flex items-start gap-2 px-3 py-2 rounded-md transition-colors ${
                            isEditing ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'
                          } ${checked ? 'bg-white border border-gray-200' : 'opacity-50'}`}
                        >
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePerm(perm.key)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                            />
                          ) : (
                            <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded flex items-center justify-center ${checked ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-300'}`}>
                              {checked ? (
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              ) : (
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                              )}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-700">{perm.label}</span>
                            <p className="text-xs text-gray-400 mt-0.5">{perm.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default RolesTab;
